/**
 * Minimal G-code → toolpath segment builder for the 3D simulator.
 *
 * Interprets modal motion (G0/G1/G2/G3), distance mode (G90/G91), units
 * (G20/G21) and the active plane for arcs (G17 XY / G18 XZ / G19 YZ). Each
 * emitted segment carries the source `lineIndex` so the viewer can sync with
 * `motionLine`.
 *
 * This intentionally only tracks the X/Y/Z tool-tip path (no A/B/C orientation)
 * — enough to visualise the cutting path on a 5-axis program.
 */

export type Vec3 = readonly [number, number, number];

export interface ToolpathSegment {
  readonly from: Vec3;
  readonly to: Vec3;
  /** Rapid (G0) moves are drawn dim/dashed, cutting moves coloured. */
  readonly rapid: boolean;
  /** Index into the source line array — used to sync with motionLine. */
  readonly lineIndex: number;
}

export interface Toolpath {
  readonly segments: readonly ToolpathSegment[];
  readonly min: Vec3;
  readonly max: Vec3;
}

const WORD_RE = /([A-Za-z])\s*(-?\d*\.?\d+)/g;
const ARC_TOLERANCE = Math.PI / 24; // ~7.5° per segment

/** Remove `( ... )` block comments and `; ...` line comments. */
function stripComment(line: string): string {
  const noBlock = line.replace(/\([^)]*\)/g, ' ');
  const semi = noBlock.indexOf(';');
  return semi >= 0 ? noBlock.slice(0, semi) : noBlock;
}

interface ArcOffsets {
  readonly i?: number;
  readonly j?: number;
  readonly k?: number;
}

/**
 * Interpolate a G2/G3 arc into a list of points for the active plane.
 *
 * The maths is identical in every plane once expressed in a right-handed 2D
 * frame (u, v) whose normal (+w) faces the viewer: G17→(X,Y)/Z, G18→(Z,X)/Y,
 * G19→(Y,Z)/X. Picking the axis order so `u × v = +w` lets the same
 * clockwise/anticlockwise angle logic work for all three planes.
 */
function interpolateArc(
  start: Vec3,
  end: Vec3,
  offsets: ArcOffsets,
  clockwise: boolean,
  plane: number,
): Vec3[] {
  const [sx, sy, sz] = start;
  const [ex, ey, ez] = end;

  // Map (x,y,z) → in-plane (u,v) + linear w, plus the matching center offsets.
  let su: number, sv: number, sw: number;
  let eu: number, ev: number, ew: number;
  let offU: number | undefined, offV: number | undefined;
  if (plane === 18) {
    su = sz;
    sv = sx;
    sw = sy;
    eu = ez;
    ev = ex;
    ew = ey;
    offU = offsets.k;
    offV = offsets.i;
  } else if (plane === 19) {
    su = sy;
    sv = sz;
    sw = sx;
    eu = ey;
    ev = ez;
    ew = ex;
    offU = offsets.j;
    offV = offsets.k;
  } else {
    su = sx;
    sv = sy;
    sw = sz;
    eu = ex;
    ev = ey;
    ew = ez;
    offU = offsets.i;
    offV = offsets.j;
  }

  // Only the center-offset (IJK) form is supported; R-only arcs fall back to a
  // straight line (rare in CAM output).
  if (offU === undefined && offV === undefined) {
    return [end];
  }

  const cu = su + (offU ?? 0);
  const cv = sv + (offV ?? 0);
  const radius = Math.hypot(su - cu, sv - cv);

  const startAngle = Math.atan2(sv - cv, su - cu);
  const endAngle = Math.atan2(ev - cv, eu - cu);

  let delta = endAngle - startAngle;
  if (clockwise) {
    if (delta >= 0) delta -= 2 * Math.PI;
  } else if (delta <= 0) {
    delta += 2 * Math.PI;
  }

  const steps = Math.max(2, Math.ceil(Math.abs(delta) / ARC_TOLERANCE));
  const points: Vec3[] = [];
  for (let s = 1; s <= steps; s++) {
    const t = s / steps;
    const ang = startAngle + delta * t;
    const u = cu + radius * Math.cos(ang);
    const v = cv + radius * Math.sin(ang);
    const w = sw + (ew - sw) * t;
    // Map (u,v,w) back to (x,y,z) for the active plane.
    if (plane === 18) points.push([v, w, u]);
    else if (plane === 19) points.push([w, u, v]);
    else points.push([u, v, w]);
  }
  return points;
}

export function buildToolpath(lines: readonly string[]): Toolpath {
  const segments: ToolpathSegment[] = [];

  let absolute = true;
  let motion = 0; // modal G0/1/2/3
  let unitScale = 1; // 1 = mm (G21), 25.4 = inch (G20)
  let plane = 17; // G17 XY

  let px = 0;
  let py = 0;
  let pz = 0;

  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  const grow = (x: number, y: number, z: number): void => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  };
  grow(px, py, pz);

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const raw = lines[lineIndex];
    if (raw === undefined) continue;
    const clean = stripComment(raw);
    if (clean.trim() === '') continue;

    let tx = px;
    let ty = py;
    let tz = pz;
    let hasMove = false;
    let offsetI: number | undefined;
    let offsetJ: number | undefined;
    let offsetK: number | undefined;

    WORD_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = WORD_RE.exec(clean)) !== null) {
      const letter = (match[1] ?? '').toUpperCase();
      const value = Number.parseFloat(match[2] ?? '0');
      if (Number.isNaN(value)) continue;

      switch (letter) {
        case 'G': {
          const g = Math.round(value);
          if (g === 0 || g === 1 || g === 2 || g === 3) motion = g;
          else if (g === 90) absolute = true;
          else if (g === 91) absolute = false;
          else if (g === 20) unitScale = 25.4;
          else if (g === 21) unitScale = 1;
          else if (g === 17 || g === 18 || g === 19) plane = g;
          break;
        }
        case 'X':
          tx = absolute ? value * unitScale : px + value * unitScale;
          hasMove = true;
          break;
        case 'Y':
          ty = absolute ? value * unitScale : py + value * unitScale;
          hasMove = true;
          break;
        case 'Z':
          tz = absolute ? value * unitScale : pz + value * unitScale;
          hasMove = true;
          break;
        case 'I':
          offsetI = value * unitScale;
          break;
        case 'J':
          offsetJ = value * unitScale;
          break;
        case 'K':
          offsetK = value * unitScale;
          break;
        default:
          break;
      }
    }

    const isArc = motion === 2 || motion === 3;
    const hasArcOffset = offsetI !== undefined || offsetJ !== undefined || offsetK !== undefined;
    // A full-circle arc may specify only the center offsets (no end point), so
    // an arc with offsets still counts as motion even when X/Y/Z are absent.
    if (!hasMove && !(isArc && hasArcOffset)) continue;

    if (isArc) {
      const points = interpolateArc(
        [px, py, pz],
        [tx, ty, tz],
        { i: offsetI, j: offsetJ, k: offsetK },
        motion === 2,
        plane,
      );
      let fx = px;
      let fy = py;
      let fz = pz;
      for (const [qx, qy, qz] of points) {
        segments.push({ from: [fx, fy, fz], to: [qx, qy, qz], rapid: false, lineIndex });
        grow(qx, qy, qz);
        fx = qx;
        fy = qy;
        fz = qz;
      }
    } else {
      segments.push({ from: [px, py, pz], to: [tx, ty, tz], rapid: motion === 0, lineIndex });
      grow(tx, ty, tz);
    }

    px = tx;
    py = ty;
    pz = tz;
  }

  if (!Number.isFinite(minX)) {
    return { segments, min: [0, 0, 0], max: [0, 0, 0] };
  }

  return {
    segments,
    min: [minX, minY, minZ],
    max: [maxX, maxY, maxZ],
  };
}
